import ClipboardIcon from '@heroicons/react/outline/esm/ClipboardIcon.js'
import CogIcon from '@heroicons/react/outline/esm/CogIcon.js'
import DocumentDuplicateIcon from '@heroicons/react/outline/esm/DocumentDuplicateIcon.js'
import DotsHorizontalIcon from '@heroicons/react/outline/esm/DotsHorizontalIcon.js'
import PencilIcon from '@heroicons/react/outline/esm/PencilIcon.js'
import PlusIcon from '@heroicons/react/outline/esm/PlusIcon.js'
import TrashIcon from '@heroicons/react/outline/esm/TrashIcon.js'
import { useDecision } from '@optimizely/react-sdk'
import { clsx } from 'clsx'
import { useClipboard } from 'use-clipboard-copy'

import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useSlate } from '@tribeplatform/react-slate-kit/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { FeaturesList } from '../../../../providers/optimizely/feature.list.js'
import { EditorSlateExtraContext } from '../types.js'
import { getBlockToCopy, getBlockToPaste, getClipboardText } from './utils.js'

export type BlockActionsProps = {
  id: string
  removable: boolean
  editable: boolean
  acceptsChildren: boolean
  hide?: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
  asDropdown?: boolean
  onRename?: () => void
}

export const BlockActions = ({
  id,
  removable,
  editable,
  acceptsChildren,
  hide = false,
  className,
  size = 'sm',
  asDropdown = false,
  onRename,
}: BlockActionsProps) => {
  const { $t } = useI18n()
  const {
    removeBlock,
    addBlock,
    compiledSlate: { blocks },
    extraContext: {
      slate: { activateBlock, openAddBlock },
    },
  } = useSlate<EditorSlateExtraContext>()
  const [decisionBlockCopyPaste] = useDecision(FeaturesList.BlockCopyPaste)

  const { copy } = useClipboard()

  const iconClassSize = clsx(
    size === 'sm' && 'w-4 h-4',
    size === 'md' && 'w-5 h-5',
    size === 'lg' && 'w-6 h-6',
  )

  const onRemove = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation()
    const confirmed = await confirm({
      title: $t({
        defaultMessage: 'Are you sure you want to delete this block?',
        id: 'Admin.Editor.Block.Delete.Confirmation',
      }),
      proceedLabel: $t({
        defaultMessage: 'Delete',
        id: 'Generics.Delete',
      }),
      cancelLabel: $t({
        defaultMessage: 'Cancel',
        id: 'Generics.Cancel',
      }),
      danger: true,
    })
    if (!confirmed) {
      return
    }
    removeBlock(id)
  }

  const onActivate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    activateBlock(id)
  }

  const onAddBlock = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    openAddBlock(id)
  }

  const onCopy = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    const blockToCopy = getBlockToCopy(id, blocks)
    if (!blockToCopy) {
      return
    }

    copy(JSON.stringify(blockToCopy))
  }

  const onPaste = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation()
    // The error is handled inside getClipboardText
    const clipboardText = await getClipboardText()
    if (clipboardText === null) {
      toast({
        title: 'Unable to paste block',
        status: 'error',
        description:
          'Please make sure your browser supports clipboard and you have allowed proper access.',
      })
      return
    }

    const newBlock = getBlockToPaste(clipboardText, blocks)
    if (newBlock === null) {
      toast({
        title: 'Unable to paste block',
        status: 'error',
        description: 'Please copy a block before using the paste block action.',
      })
      return
    }

    addBlock(id, 0, {
      blocks: newBlock.blocks,
      rootBlock: newBlock.rootId,
    })
  }

  return (
    <div
      className={clsx(
        'flex flex-row items-center transition-all',
        size === 'sm' && 'gap-0.5',
        size === 'md' && 'gap-1',
        size === 'lg' && 'gap-2',
        hide || (!removable && !editable && !acceptsChildren)
          ? 'opacity-0'
          : 'opacity-100',
        className,
      )}
    >
      {acceptsChildren && (
        <Button
          size="xs"
          variant="basic"
          rounded
          onClick={onAddBlock}
          leadingIcon={<PlusIcon />}
          className="p-1 bg-transparent hover:bg-surface-hovered"
        ></Button>
      )}
      {!acceptsChildren && asDropdown && editable && (
        <Button
          size="xs"
          variant="basic"
          rounded
          onClick={onActivate}
          leadingIcon={<CogIcon />}
          className="p-1 bg-transparent hover:bg-surface-hovered"
        ></Button>
      )}
      {asDropdown ? (
        <Dropdown placement="bottom-end">
          <Dropdown.ButtonMinimal as="div" className="bg-transparent">
            <Button
              size="xs"
              variant="basic"
              rounded
              leadingIcon={<DotsHorizontalIcon className={iconClassSize} />}
              className="p-1 bg-transparent hover:bg-surface-hovered"
            />
          </Dropdown.ButtonMinimal>
          <Dropdown.Items>
            {acceptsChildren && (
              <Dropdown.Item onClick={onAddBlock} leadingIcon={<PlusIcon />}>
                <T defaultMessage="Add block" id="BlockActions.AddBlock" />
              </Dropdown.Item>
            )}
            {editable && (
              <Dropdown.Item onClick={onActivate} leadingIcon={<CogIcon />}>
                <T defaultMessage="Settings" id="BlockActions.Settings" />
              </Dropdown.Item>
            )}
            {editable && typeof onRename === 'function' && (
              <Dropdown.Item onClick={onRename} leadingIcon={<PencilIcon />}>
                <T defaultMessage="Rename" id="BlockActions.Rename" />
              </Dropdown.Item>
            )}
            {decisionBlockCopyPaste.enabled && (
              <Dropdown.Item
                onClick={onCopy}
                leadingIcon={<DocumentDuplicateIcon />}
              >
                <T defaultMessage="Copy block" id="BlockActions.CopyBlock" />
              </Dropdown.Item>
            )}
            {decisionBlockCopyPaste.enabled && acceptsChildren && (
              <Dropdown.Item onClick={onPaste} leadingIcon={<ClipboardIcon />}>
                <T defaultMessage="Paste block" id="BlockActions.PasteBlock" />
              </Dropdown.Item>
            )}
            {removable && (
              <Dropdown.Item onClick={onRemove} leadingIcon={<TrashIcon />}>
                <T defaultMessage="Remove" id="Generics.Remove" />
              </Dropdown.Item>
            )}
          </Dropdown.Items>
        </Dropdown>
      ) : (
        <>
          {removable && (
            <button
              tabIndex={0}
              className="p-1 rounded-full hover:bg-background-hovered"
              type="button"
              onClick={onRemove}
            >
              <TrashIcon className={iconClassSize} />
            </button>
          )}
          {editable && (
            <button
              tabIndex={0}
              className="p-1 rounded-full hover:bg-background-hovered"
              type="button"
              onClick={onActivate}
            >
              <CogIcon className={iconClassSize} />
            </button>
          )}
          {acceptsChildren && (
            <button
              tabIndex={0}
              className="p-1 rounded-full hover:bg-background-hovered"
              type="button"
              onClick={onAddBlock}
            >
              <PlusIcon className={iconClassSize} />
            </button>
          )}
        </>
      )}
    </div>
  )
}
